// src/components/music/parts/PlaylistViewer.jsx

import { motion } from 'framer-motion';
import { Play, Pause, X, Trash2, Music, Volume2, Download, CheckCircle } from 'lucide-react';

export const PlaylistViewer = ({
  playlist,
  onClose,
  onPlayTrack,
  onRemoveTrack,
  onPauseTrack,
  isPlaying,
  currentTrack,
  currentUser,
  isPurchased,
  onDownload
}) => {
  if (!playlist) return null;

  const handlePlayPlaylist = () => {
    if (playlist.tracks && playlist.tracks.length > 0) {
      onPlayTrack(playlist.tracks[0], 0);
    }
  };

  const handleTrackButtonClick = (track, index) => {
    if (currentTrack?.id === track.id && isPlaying) {
      onPauseTrack?.();
    } else {
      onPlayTrack(track, index);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gray-900 rounded-lg shadow-2xl w-full h-full max-h-[95vh] md:max-h-[90vh] md:max-w-4xl md:rounded-lg overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-purple-500 p-4 md:p-8 flex items-center justify-between sticky top-0">
          <div className="flex items-center gap-3 md:gap-6 flex-1 min-w-0">
            <Music size={24} className="text-white flex-shrink-0 md:w-8 md:h-8" />
            <div className="min-w-0 flex-1">
              <h3 className="text-lg md:text-3xl font-semibold text-white line-clamp-2">{playlist.name}</h3>
              <p className="text-xs md:text-base text-white/80 mt-0.5 md:mt-1">
                {playlist.tracks?.length || 0} track
                {playlist.tracks?.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-full transition flex-shrink-0"
          >
            <X size={24} className="md:w-7 md:h-7" />
          </button>
        </div>

        {/* Description */}
        {playlist.description && (
          <div className="px-4 md:px-8 pt-2 md:pt-3 pb-2 border-b border-white/10">
            <p className="text-xs text-gray-400 line-clamp-2">{playlist.description}</p>
          </div>
        )}

        {/* Tracks List */}
        <div className="flex-1 overflow-y-auto">
          {!playlist.tracks || playlist.tracks.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-400">
              <Music size={48} className="mb-3 opacity-50" />
              <p className="text-lg">No tracks in this playlist</p>
            </div>
          ) : (
            <div className="space-y-1 md:space-y-2 p-3 md:p-8">
              {playlist.tracks.map((track, index) => {
                const purchased = isPurchased(track.id);
                return (
                  <motion.div
                    key={track.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`flex items-center gap-2 md:gap-4 p-2 md:p-4 rounded-lg group hover:bg-white/10 transition cursor-pointer ${
                      currentTrack?.id === track.id
                        ? 'bg-green-500/20 border border-green-500/50'
                        : ''
                    }`}
                  >
                    {/* Track Number / Playing Icon */}
                    {currentTrack?.id === track.id && isPlaying ? (
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 0.8, repeat: Infinity }}
                        className="w-6 md:w-8 flex-shrink-0 flex items-center justify-center"
                      >
                        <Volume2 size={16} className="md:w-5 md:h-5 text-green-500" />
                      </motion.div>
                    ) : (
                      <span className="text-xs md:text-sm text-gray-500 w-6 md:w-8 text-center flex-shrink-0 font-medium">
                        {index + 1}
                      </span>
                    )}

                    {/* Track Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm md:text-lg font-medium text-white truncate">
                          {track.name}
                        </p>
                        {purchased && (
                          <CheckCircle size={14} className="md:w-4 md:h-4 text-green-400 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs md:text-sm text-gray-400 truncate">
                        {track.artist || 'The Asidors'}
                      </p>
                    </div>

                    {/* Download Button (if purchased) */}
                    {purchased && currentUser ? (
                      <button
                        onClick={() => onDownload(track)}
                        className="text-gray-400 hover:text-green-400 transition p-1.5 md:p-2 rounded-full hover:bg-white/10 flex-shrink-0"
                        title="Download"
                      >
                        <Download size={16} className="md:w-5 md:h-5" />
                      </button>
                    ) : (
                      /* Play Button (if not purchased) */
                      <button
                        onClick={() => handleTrackButtonClick(track, index)}
                        className="text-gray-400 hover:text-green-400 transition p-1.5 md:p-2 rounded-full hover:bg-white/10 flex-shrink-0"
                        title={currentTrack?.id === track.id && isPlaying ? "Pause" : "Play"}
                      >
                        {currentTrack?.id === track.id && isPlaying ? (
                          <Pause size={16} className="md:w-5 md:h-5" fill="currentColor" />
                        ) : (
                          <Play size={16} className="md:w-5 md:h-5" fill="currentColor" />
                        )}
                      </button>
                    )}

                    {/* Remove Button */}
                    <button
                      onClick={() => onRemoveTrack(playlist.id, track.id)}
                      className="text-gray-400 hover:text-red-400 transition p-1.5 md:p-2 rounded-full hover:bg-white/10 flex-shrink-0"
                      title="Remove"
                    >
                      <Trash2 size={16} className="md:w-5 md:h-5" />
                    </button>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {playlist.tracks && playlist.tracks.length > 0 && (
          <div className="p-3 md:p-4 border-t border-white/10">
            <button
              onClick={handlePlayPlaylist}
              className="w-full px-4 py-2 md:py-3 bg-gradient-to-r from-green-500 to-purple-500 text-white rounded-lg hover:shadow-lg transition font-medium flex items-center justify-center gap-2 text-sm md:text-base"
            >
              <Play size={16} className="md:w-4 md:h-4" fill="currentColor" />
              Play Playlist
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};